/**
 * `<DslInput>` — a syntax-highlighted expression box, with no editor dependency.
 *
 * A real code editor (CodeMirror and friends) would be a heavy addition to an
 * island that has to stay WebView-hostable on three other platforms (D18). The
 * classic underlay does the job instead: a transparent `<textarea>` laid exactly
 * over a coloured `<pre>`, so the browser still owns the caret, selection,
 * IME, undo and accessibility, and only the *paint* is ours.
 *
 * Everything about the two layers must match to the pixel or the colours drift
 * away from the glyphs, so font, padding, border, line-height and wrapping are
 * defined once in `sharedTextStyle` and spread into both. That shared constant
 * is not tidiness — it is the correctness condition.
 *
 * The payoff beyond colour is that a mistake is visible *before* submitting:
 * an identifier this screen does not have is painted as an error, and a compile
 * failure's character position is underlined where it happened rather than
 * described in prose.
 *
 * Deliberately **not** a rich-text editor. Quill and friends are for formatted
 * documents — they emit HTML or a delta rather than plain text, and know nothing
 * of a custom grammar's tokens, so the integration would be spent extracting a
 * string back out. The comparable tool is a code editor (CodeMirror), which buys
 * completion and bracket matching on top of what is here, at the cost of a
 * dependency this island would then carry into every WebView host.
 */

import {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  type CSSProperties,
} from "react";

import { TOKEN_COLORS, tokenizeDsl, type HighlightOptions } from "../lib/dsl-highlight.js";
import { applyCompletion, completionsFor, type CompletionQuery } from "../lib/dsl-complete.js";
import { DEFAULT_CATALOG, screenerCatalog, type DslCatalog } from "../lib/dsl-catalog.js";
import { useState } from "react";

/** What a caller can do to the editor from outside it. */
export interface DslInputHandle {
  /**
   * Insert at the caret, replacing any selection, and leave the caret after it.
   *
   * Exposed imperatively because the caret is the textarea's and nobody else's:
   * a reference panel inserting by rewriting `value` would drop the cursor to
   * the end and undo the point of clicking where you were typing.
   */
  insert: (text: string) => void;
}

export interface DslInputProps {
  value: string;
  onChange: (next: string) => void;
  /** Enter commits. Escape reverts, and is the caller's to define. */
  onSubmit: () => void;
  onRevert: () => void;
  placeholder?: string;
  /** Character offset to underline, from a compile error. -1 for none. */
  errorAt?: number;
  functions?: HighlightOptions["functions"];
  fields?: HighlightOptions["fields"];
  /** Vocabulary for completion. Highlighting uses `functions`/`fields`. */
  catalog?: DslCatalog;
}

/**
 * Every property that affects glyph position. Shared by both layers, because
 * any difference between them shows up as colour sliding off the text.
 */
const sharedTextStyle: CSSProperties = {
  margin: 0,
  padding: "0.25rem 0.4rem",
  border: "1px solid transparent",
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: "0.78rem",
  lineHeight: 1.5,
  letterSpacing: "normal",
  tabSize: 2,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  overflowWrap: "break-word",
};

export const DslInput = forwardRef<DslInputHandle, DslInputProps>(function DslInput(
  {
    value,
    onChange,
    onSubmit,
    onRevert,
    placeholder,
    errorAt = -1,
    functions,
    fields,
    catalog: injectedCatalog = DEFAULT_CATALOG,
  },
  handleRef,
) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const underlayRef = useRef<HTMLPreElement>(null);
  const [query, setQuery] = useState<CompletionQuery | null>(null);
  /**
   * Which suggestion is chosen, or -1 for none.
   *
   * Starts at none and only moves when the arrows are pressed. Highlighting the
   * first match by default means Enter accepts something the typist never
   * asked for: `sma20` is a complete, valid expression, and with `sma200` on
   * the list Enter would silently replace one with the other. A suggestion is
   * an offer until someone selects it.
   */
  const [active, setActive] = useState(-1);

  const fieldList = useMemo(() => [...(fields ?? [])], [fields]);

  // Narrowed to what a screen can answer: completing a Symbol Indicator would
  // insert a call that comes back blank, and nothing in the list says so. See
  // `screenerCatalog`.
  const catalog = useMemo(() => screenerCatalog(injectedCatalog), [injectedCatalog]);

  /** Recompute suggestions from wherever the caret now is. */
  const refresh = (text: string, caret: number): void => {
    const next = completionsFor(text, caret, catalog, fieldList);
    setQuery(next.items.length > 0 ? next : null);
    setActive(-1);
  };

  const accept = (index: number): void => {
    const textarea = textareaRef.current;
    if (!query || !textarea) return;
    const choice = query.items[index];
    if (!choice) return;
    const applied = applyCompletion(value, query, choice);
    onChange(applied.text);
    setQuery(null);
    queueMicrotask(() => {
      textarea.focus();
      textarea.setSelectionRange(applied.caret, applied.caret);
    });
  };

  useImperativeHandle(handleRef, () => ({
    insert(text: string) {
      const textarea = textareaRef.current;
      if (!textarea) return;
      const start = textarea.selectionStart ?? value.length;
      const end = textarea.selectionEnd ?? start;
      onChange(value.slice(0, start) + text + value.slice(end));
      // After the state lands, put the caret past what was inserted — otherwise
      // clicking a second entry appends to the wrong place.
      queueMicrotask(() => {
        textarea.focus();
        const at = start + text.length;
        textarea.setSelectionRange(at, at);
      });
    },
  }));

  // Long expressions scroll; the underlay has to follow or the colours lag the
  // text by however far the caret has moved.
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    const underlay = underlayRef.current;
    if (!textarea || !underlay) return;
    underlay.scrollTop = textarea.scrollTop;
    underlay.scrollLeft = textarea.scrollLeft;
  }, [value]);

  const tokens = tokenizeDsl(value, { functions, fields });

  return (
    <div style={container}>
      <pre ref={underlayRef} aria-hidden="true" style={{ ...sharedTextStyle, ...underlay }}>
        {tokens.map((token, i) => {
          const end = token.start + token.text.length;
          const marked = errorAt >= token.start && errorAt < end;
          return (
            <span
              key={i}
              style={{
                color: TOKEN_COLORS[token.kind],
                // Underline exactly where the compiler stopped, rather than
                // making the reader count characters from a message.
                ...(marked
                  ? { textDecoration: "underline wavy var(--qf-negative)", textUnderlineOffset: 3 }
                  : {}),
              }}
            >
              {token.text}
            </span>
          );
        })}
        {/* A trailing newline is not rendered by `pre`, so the underlay would be
            one line short of the textarea and scroll out of step. */}
        {value.endsWith("\n") ? "\n" : ""}
      </pre>

      <textarea
        ref={textareaRef}
        aria-label="ScanScript expression"
        value={value}
        spellCheck={false}
        autoComplete="off"
        autoCapitalize="off"
        autoCorrect="off"
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value);
          refresh(e.target.value, e.target.selectionStart ?? e.target.value.length);
        }}
        onBlur={() => setQuery(null)}
        onScroll={() => {
          const textarea = textareaRef.current;
          const under = underlayRef.current;
          if (textarea && under) {
            under.scrollTop = textarea.scrollTop;
            under.scrollLeft = textarea.scrollLeft;
          }
        }}
        onKeyDown={(e) => {
          // While the popup is open it owns the keys that would otherwise
          // commit or revert — accepting a suggestion must not also submit.
          if (query) {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActive((i) => (i + 1) % query.items.length);
              return;
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              setActive((i) => (i <= 0 ? query.items.length : i) - 1);
              return;
            }
            if (e.key === "Tab") {
              // Tab is the explicit accept gesture, so it takes the first match
              // when nothing has been selected — that is what it is *for*.
              e.preventDefault();
              accept(active >= 0 ? active : 0);
              return;
            }
            if (e.key === "Enter" && active >= 0) {
              // Only when something was actually chosen. Otherwise Enter falls
              // through and submits what the user typed, which is the whole
              // point: a list of near-misses must not overrule them.
              e.preventDefault();
              accept(active);
              return;
            }
            if (e.key === "Escape") {
              e.preventDefault();
              setQuery(null);
              return;
            }
          }
          if (e.key === "Enter" && !e.shiftKey) {
            // Enter commits rather than inserting a newline: this is one
            // expression, not a document.
            e.preventDefault();
            onSubmit();
          } else if (e.key === "Escape") {
            onRevert();
          }
        }}
        style={{ ...sharedTextStyle, ...editable }}
      />

      {query && (
        // `onMouseDown` rather than `onClick`: the textarea's blur fires first
        // and would close the popup before a click could land on it.
        <ul style={popup} role="listbox" aria-label="Completions">
          <li style={popupHint}>↑↓ to choose · Tab accepts · Enter applies what you typed</li>
          {query.items.map((item, i) => (
            <li key={item.value}>
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  accept(i);
                }}
                style={{
                  ...option,
                  background: i === active ? "var(--qf-selection)" : "transparent",
                }}
              >
                <code style={{ color: item.kind === "function" ? "var(--qf-accent)" : "inherit" }}>
                  {item.value}
                </code>
                <span style={{ marginLeft: "auto", color: "var(--qf-text-muted)" }}>
                  {item.detail}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

const container: CSSProperties = {
  position: "relative",
  // Fills the space its parent gives it, rather than sizing to one row. An
  // expression box that is one line tall makes anything non-trivial unreadable
  // while writing it.
  flex: 1,
  minWidth: 0,
  minHeight: "4rem",
  background: "var(--qf-background)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  overflow: "hidden",
};

const popup: CSSProperties = {
  position: "absolute",
  left: "0.4rem",
  bottom: "0.3rem",
  zIndex: 5,
  minWidth: "14rem",
  // **Never taller than the box it sits in, less a couple of lines.**
  //
  // The popup is pinned to the bottom and grows upward, so its height is height
  // taken off the top — where the text is. `9rem` alone was a cap against a box
  // whose `minHeight` is `4rem`: a dozen completions then covered the whole
  // editor, including the characters being typed to produce them, so the list
  // narrowed as you typed and you could not see what you had typed to narrow
  // it. The `calc` keeps the first lines visible whatever the box's height,
  // and `9rem` still caps it in a tall one.
  maxHeight: "min(9rem, calc(100% - 2.5rem))",
  overflowY: "auto",
  margin: 0,
  padding: "0.15rem",
  listStyle: "none",
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
};

const popupHint: CSSProperties = {
  padding: "0.1rem 0.35rem",
  color: "var(--qf-text-muted)",
  fontSize: "0.62rem",
  borderBottom: "1px solid var(--qf-border)",
  marginBottom: "0.1rem",
};

const option: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  width: "100%",
  padding: "0.1rem 0.35rem",
  border: "none",
  borderRadius: 3,
  color: "var(--qf-text)",
  fontSize: "0.72rem",
  textAlign: "left",
  cursor: "pointer",
};

const underlay: CSSProperties = {
  position: "absolute",
  inset: 0,
  overflow: "auto",
  pointerEvents: "none",
  // `pre` defaults to a UA font; the shared style sets ours, and this stops the
  // browser adding its own background behind the coloured text.
  background: "transparent",
};

const editable: CSSProperties = {
  position: "relative",
  display: "block",
  width: "100%",
  height: "100%",
  // Resizing is the container's job — a drag handle here would move the
  // textarea out from under the underlay and the colours would stop lining up.
  resize: "none",
  background: "transparent",
  // The glyphs come from the underlay; only the caret and selection are the
  // textarea's, which is what keeps native editing behaviour intact.
  color: "transparent",
  caretColor: "var(--qf-text)",
  outline: "none",
  overflow: "auto",
};
